import { Client } from "@/Common/Api/Client";
import { ClientResponse } from "@/Common/Api/ClientResponse";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { DateHelper } from "@/Common/Helpers/DateHelper";
import { FormModificationState } from "@/Common/Models/UI/Forms/FormModificationState";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ValueSetType } from "../Constants/ValueSetType";
import { CodeValue } from "../Models/CodeValue";
import { ConditionModel } from "../Models/ConditionModel";
import { ImmunizationModel } from "../Models/ImmunizationModel";
import { LocationModel } from "../Models/LocationModel";
import { RvqLocationPreference } from "../Models/RvqLocationPreference";
import { RvqMedicationPreference } from "../Models/RvqMedicationPreference";
import { RvqPractitionerPreference } from "../Models/RvqPractitionerPreference";
import { GetValuesRequest } from "../RequestModels/GetValuesRequest";
import { GetConditionModelResponse } from "../ResponseModels/GetConditionModelResponse";
import { GetRvqLocationByIdResponse } from "../ResponseModels/GetRvqLocationByIdResponse";
import { GetRvqLocationsBySearchResponse } from "../ResponseModels/GetRvqLocationsBySearchResponse";
import { GetValuesResponse } from "../ResponseModels/GetValuesResponse";
import { RvqTransactionResponse } from "../ResponseModels/RvqTransactionResponse";
import { ImmunizationProfileSectionPopup } from "./ImmunizationProfileSectionPopup";

interface IBaseImmunizationProfileSectionProps {
    id: string;
    title: string;
    width: number;
    height?: number;
    top?: number;
    formModificationMode: FormModificationState;
    locationPreferences?: RvqLocationPreference[];
    onClose: () => void;
    patientId: number;
    updateConditionModel?: (model: ConditionModel, appliedLocation?: RvqLocationPreference) => void;
    updateImmunizationModel?: (model: ImmunizationModel) => void;
    updatePreferences?: (
        preferences: RvqMedicationPreference[] | null,
        practitionerPreferences: RvqPractitionerPreference[] | null,
        locationPreferences: RvqLocationPreference[] | null,
        then?: () => void
    ) => void;
}

class BaseImmunizationProfileSectionState {
    public values: GetValuesResponse;
    public isLoading: boolean;

    /**
     * The unique RVQ transaction ID for the last request, if applicable. This should be included when resubmitting a failed request.
     */
    public cachedTransactionId: string;

    constructor(init?: Partial<BaseImmunizationProfileSectionState>) {
        Object.assign(this, init);
    }

    public static default(): BaseImmunizationProfileSectionState {
        return new BaseImmunizationProfileSectionState({
            values: GetValuesResponse.empty(),
            isLoading: false
        });
    }
}

abstract class BaseImmunizationProfileSection<TState extends BaseImmunizationProfileSectionState, TProps extends IBaseImmunizationProfileSectionProps> extends BaseReactComponent<TProps, TState> {
    protected readonly dateFormatForDateControl = "yyyy-MM-dd";

    public componentDidMount(then?: () => void): void {
        this.getValues(then);
    }

    public render(): JSX.Element {
        return (
            <ImmunizationProfileSectionPopup
                {...this.props}
                isModifying={this.isEditing || this.isNew}
                isValid={this.canSave()}
                isLoading={this.state.isLoading}
                onSave={() => this.onSave()}
                modifyButtonLabel={this.setModifyButtonLabel()}
            >
                {this.getBody()}
            </ImmunizationProfileSectionPopup>
        );
    }

    protected abstract getBody(): JSX.Element;

    protected abstract onSave(): Promise<void>;

    protected abstract get requestValues(): GetValuesRequest;

    protected canSave(): boolean {
        return true;
    }

    protected formatDateForMoment(date: Date): string {
        return DateHelper.formatIsoDateOnly(date);
    }

    public async getValues(then?: () => void): Promise<void> {
        if (this.requestValues) {
            const response = await Client.postAsync<GetValuesResponse>(
                ServerOperations.Server.QuebecVaccinationRegistry_GetValues,
                this.requestValues
            );
            this.mutateState(
                s => s.values = response.isSuccess
                    ? response.data
                    : GetValuesResponse.empty(),
                () => then?.()
            );
        }
    }

    public async handleLocationSearch(text: string): Promise<LocationModel[]> {
        const response = await Client.postAsync<GetRvqLocationsBySearchResponse>(
            ServerOperations.Server.QuebecVaccinationRegistry_GetLocationsBySearch,
            {
                Name: text,
                CityName: null
            }
        );
        return response.isSuccess
            ? response.data.Locations
            : [];
    }

    public getCodeValue(type: ValueSetType, code: string): CodeValue {
        const lookup = this.state.values?.Types[type.toString()];
        const value = lookup?.[code];
        if (!value)
            return null;

        return new CodeValue({
            Code: code,
            Label: value?.Label
        });
    }

    public async getLocationById(id: number): Promise<LocationModel> {
        const response = await Client.postAsync<GetRvqLocationByIdResponse>(
            ServerOperations.Server.QuebecVaccinationRegistry_GetLocationById,
            {
                Id: id
            }
        );
        return response.isSuccess
            ? response.data.Location
            : null;
    }

    public async saveConditionModel(conditionModel: ConditionModel): Promise<void> {
        const response = await Client.postAsync<GetConditionModelResponse>(
            this.isNew
                ? ServerOperations.Server.QuebecVaccinationRegistry_AddCondition
                : ServerOperations.Server.QuebecVaccinationRegistry_SetConditionEndDate,

            this.isNew
                ? {
                    Condition: conditionModel,
                    TransactionId: this.state.cachedTransactionId
                }
                : {
                    Id: conditionModel.Id,
                    PatientId: conditionModel.PatientId,
                    EndDate: conditionModel.DatePeriod.End,
                    Version: conditionModel.Metadata.VersionId,
                    TransactionId: this.state.cachedTransactionId
                }
        );

        this.handleRvqResponse(response, () => this.props.updateConditionModel(response.data?.Condition, response.data?.AppliedLocation));
    }

    protected get isNew(): boolean {
        return this.props.formModificationMode === FormModificationState.Creating;
    }

    protected get isEditing(): boolean {
        return this.props.formModificationMode === FormModificationState.Editing;
    }

    protected get isViewing(): boolean {
        return this.props.formModificationMode === FormModificationState.Viewing;
    }

    protected setModifyButtonLabel(): string {
        return "SaveLabel";
    }

    protected handleRvqResponse(response: ClientResponse<RvqTransactionResponse>, onUpdate: () => void): void {
        this.mutateState(s => s.cachedTransactionId = response.data?.TransactionId);
        if (response.isSuccess) {
            onUpdate();
            this.props.onClose();
        }
        else
            sessionStore.showPopup(response.errors);
    }
}

export { BaseImmunizationProfileSection, BaseImmunizationProfileSectionState, IBaseImmunizationProfileSectionProps };
